import { useEffect, useRef } from 'react'
import { DEMO_DATASETS } from '@brt/shared-logic'
import { useAppStore } from '@/store/useAppStore.ts'
import { Database, X } from 'lucide-react'

interface DemoDatasetModalProps {
  isOpen: boolean
  onClose: () => void
}

export function DemoDatasetModal({ isOpen, onClose }: DemoDatasetModalProps) {
  const loadDemoDataset = useAppStore((s) => s.loadDemoDataset)
  const modalRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isOpen) {
      closeRef.current?.focus()
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose()
      }
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleSelect = (index: number) => {
    const dataset = DEMO_DATASETS[index]
    if (dataset) {
      loadDemoDataset(dataset)
      onClose()
    }
  }

  return (
    <div
      className="demo-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Load demo dataset"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="demo-modal" ref={modalRef}>
        <div className="demo-modal__header">
          <h2 className="demo-modal__title">
            <Database size={20} />
            Load Demo Dataset
          </h2>
          <button
            ref={closeRef}
            onClick={onClose}
            className="demo-modal__close"
            aria-label="Close"
            type="button"
          >
            <X size={18} />
          </button>
        </div>
        <p className="demo-modal__subtitle">
          Select a pre-configured scenario to explore the calculator with realistic data.
        </p>
        <ul className="demo-modal__list">
          {DEMO_DATASETS.map((dataset, index) => (
            <li key={dataset.name} className="demo-modal__item">
              <button
                type="button"
                className="demo-modal__item-btn"
                onClick={() => handleSelect(index)}
              >
                <span className="demo-modal__item-name">{dataset.name}</span>
                <span className="demo-modal__item-desc">{dataset.description}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
